import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NpsCampaignDto } from '@area-restrita/api-client/model/npsCampaignDto';
import {
  useCloseNpsCampaign,
  useDeleteNpsCampaign,
  useOpenNpsCampaign,
  useUpdateNpsCampaign,
} from '@area-restrita/api-client/endpoints/nps/nps.gen';
import { PencilIcon, PlayIcon, SquareIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'react-toastify';

import Dialog from '@/components/template/Dialog';
import { Button } from '@/components/ui/button';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import NpsCampaignConfirmation from '@/modules/nps/components/NpsCampaignConfirmation';
import NpsCampaignDetailsDialog from '@/modules/nps/components/NpsCampaignDetailsDialog';
import NpsCampaignForm from '@/modules/nps/components/NpsCampaignForm';
import { NpsCampaignFormSchema } from '@/modules/nps/components/NpsCampaignForm/schema';
import NpsCampaignResultsDialog from '@/modules/nps/components/NpsCampaignResultsDialog';
import {
  getNpsResultsEligibility,
  getErrorMessage,
  isConflictError,
  toDateTimeInputValue,
  toIsoDateTime,
} from '@/modules/nps/utils';

import NpsCampaignExportButton from './NpsCampaignExportButton';

interface IProps {
  campaign: NpsCampaignDto;
}

const NpsCampaignActionsCell: React.FC<IProps> = ({ campaign }) => {
  const queryClient = useQueryClient();
  const { checkAuthorization, getUserType } = useAuthSessionStore();
  const updateMutation = useUpdateNpsCampaign();
  const deleteMutation = useDeleteNpsCampaign();
  const openMutation = useOpenNpsCampaign();
  const closeMutation = useCloseNpsCampaign();
  const userType = getUserType();

  const canViewDetails = checkAuthorization(({ can }) =>
    can('viewDetails', 'nps'),
  );
  const resultsEligibility = getNpsResultsEligibility({
    campaign,
    canViewDetails,
    userType,
  });
  const canUpdate =
    campaign.status === 'draft' &&
    checkAuthorization(({ can }) => can('update', 'nps'));
  const canDelete =
    campaign.status === 'draft' &&
    checkAuthorization(({ can }) => can('delete', 'nps'));
  const canOpen =
    campaign.status === 'draft' &&
    checkAuthorization(({ can }) => can('open', 'nps'));
  const canClose =
    campaign.status === 'open' &&
    checkAuthorization(({ can }) => can('close', 'nps'));
  const canExport = checkAuthorization(({ can }) => can('export', 'nps'));
  const canViewComments = checkAuthorization(({ can }) =>
    can('viewComments', 'nps'),
  );

  const invalidateCampaigns = () =>
    queryClient.invalidateQueries({ queryKey: ['/api/v1/nps/campaigns'] });

  const handleUpdate = async (data: NpsCampaignFormSchema) => {
    await updateMutation.mutateAsync(
      {
        id: campaign.id,
        data: {
          ...data,
          startAt: toIsoDateTime(data.startAt),
          endAt: toIsoDateTime(data.endAt),
        },
      },
      {
        onSuccess: async () => {
          toast.success('Campanha atualizada com sucesso.');
          await invalidateCampaigns();
        },
        onError: (error) =>
          toast.error(
            getErrorMessage(error, 'Erro ao atualizar campanha NPS.'),
          ),
      },
    );
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(
      { id: campaign.id },
      {
        onSuccess: async () => {
          toast.success('Campanha excluída com sucesso.');
          await invalidateCampaigns();
        },
        onError: (error) =>
          toast.error(getErrorMessage(error, 'Erro ao excluir campanha NPS.')),
      },
    );
  };

  const handleOpen = async () => {
    await openMutation.mutateAsync(
      { id: campaign.id },
      {
        onSuccess: async () => {
          toast.success('Campanha aberta com sucesso.');
          await invalidateCampaigns();
        },
        onError: (error) => {
          const message = isConflictError(error)
            ? 'Já existe uma campanha NPS aberta para esta empresa.'
            : getErrorMessage(error, 'Erro ao abrir campanha NPS.');

          toast.error(message);
        },
      },
    );
  };

  const handleClose = async () => {
    await closeMutation.mutateAsync(
      { id: campaign.id },
      {
        onSuccess: async () => {
          toast.success('Campanha encerrada com sucesso.');
          await invalidateCampaigns();
        },
        onError: (error) =>
          toast.error(getErrorMessage(error, 'Erro ao encerrar campanha NPS.')),
      },
    );
  };

  return (
    <div className="flex justify-end gap-2">
      <NpsCampaignDetailsDialog
        campaign={campaign}
        disabled={!canViewDetails}
      />

      <NpsCampaignExportButton
        campaign={campaign}
        canExport={canExport}
        canViewComments={canViewComments}
        userType={userType}
      />

      <NpsCampaignResultsDialog
        campaign={campaign}
        disabled={!resultsEligibility.enabled}
        disabledReason={resultsEligibility.disabledReason}
      />

      <Dialog
        title="Editar campanha"
        description="Somente campanhas em rascunho podem ser editadas."
        trigger={
          <Button variant="outline" size="icon-sm" disabled={!canUpdate}>
            <PencilIcon data-icon="inline-start" />
          </Button>
        }
      >
        {({ handleClose: closeDialog }) => (
          <NpsCampaignForm
            companyOption={{
              value: campaign.companyId,
              label: campaign.company.tradingName,
            }}
            defaultValues={{
              name: campaign.name,
              companyId: campaign.companyId,
              startAt: toDateTimeInputValue(campaign.startAt),
              endAt: toDateTimeInputValue(campaign.endAt),
              metricsVisibleToCompany: campaign.metricsVisibleToCompany,
              commentsVisibleToCompany: campaign.commentsVisibleToCompany,
              exportAvailableToCompany: campaign.exportAvailableToCompany,
            }}
            isSubmitting={updateMutation.isPending}
            onCancel={closeDialog}
            onSubmit={async (data) => {
              await handleUpdate(data);
              closeDialog();
            }}
          />
        )}
      </Dialog>

      <NpsCampaignConfirmation
        title="Excluir campanha?"
        description="Esta ação remove definitivamente o rascunho da campanha NPS."
        confirmLabel="Excluir"
        isDestructive
        disabled={!canDelete}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        trigger={
          <Button variant="destructive" size="icon-sm" disabled={!canDelete}>
            <Trash2Icon data-icon="inline-start" />
          </Button>
        }
      />

      <NpsCampaignConfirmation
        title="Abrir campanha?"
        description="Ao abrir a campanha, os dados elegíveis serão capturados em um snapshot e a campanha não poderá mais ser editada."
        confirmLabel="Abrir"
        disabled={!canOpen}
        isLoading={openMutation.isPending}
        onConfirm={handleOpen}
        trigger={
          <Button variant="outline" size="icon-sm" disabled={!canOpen}>
            <PlayIcon data-icon="inline-start" />
          </Button>
        }
      />

      <NpsCampaignConfirmation
        title="Encerrar campanha?"
        description="Ao encerrar a campanha, novas respostas serão bloqueadas e não será possível reabri-la."
        confirmLabel="Encerrar"
        isDestructive
        disabled={!canClose}
        isLoading={closeMutation.isPending}
        onConfirm={handleClose}
        trigger={
          <Button variant="outline" size="icon-sm" disabled={!canClose}>
            <SquareIcon data-icon="inline-start" />
          </Button>
        }
      />
    </div>
  );
};

export default NpsCampaignActionsCell;
